import {
  Activity,
  BarChart2,
  ChevronDown,
  ClipboardList,
  Cloud,
  GitBranch,
  type LucideIcon,
  Settings2,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type AdminView =
  | "admin-apps"
  | "admin-audit"
  | "admin-users"
  | "admin-salesforce"
  | "admin-git-hosts"
  | "admin-analytics"
  | "admin-health";

interface AdminMenuItem {
  view: AdminView;
  icon: LucideIcon;
  labelKey: string;
  /** true = admin only; false = admin OR operator. */
  adminOnly: boolean;
}

const ITEMS: AdminMenuItem[] = [
  { view: "admin-apps", icon: Settings2, labelKey: "nav.adminApps", adminOnly: false },
  { view: "admin-audit", icon: ClipboardList, labelKey: "nav.adminAudit", adminOnly: false },
  { view: "admin-users", icon: Users, labelKey: "nav.adminUsers", adminOnly: true },
  { view: "admin-salesforce", icon: Cloud, labelKey: "nav.adminSalesforce", adminOnly: true },
  { view: "admin-git-hosts", icon: GitBranch, labelKey: "nav.adminGitHosts", adminOnly: true },
  { view: "admin-analytics", icon: BarChart2, labelKey: "nav.adminAnalytics", adminOnly: true },
  { view: "admin-health", icon: Activity, labelKey: "nav.adminHealth", adminOnly: true },
];

interface AdminMenuProps {
  isAdmin: boolean;
  activeView: string;
  onSelect: (view: AdminView) => void;
}

export function AdminMenu({ isAdmin, activeView, onSelect }: AdminMenuProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const items = ITEMS.filter((i) => isAdmin || !i.adminOnly);
  const active = items.some((i) => i.view === activeView);

  return (
    <div className="admin-menu" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className={`nav-btn${active ? " nav-btn--active" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="admin-menu-dropdown"
        onClick={() => setOpen((v) => !v)}
      >
        {t("nav.admin")}
        <ChevronDown size={14} strokeWidth={2.5} aria-hidden="true" />
      </button>
      {open ? (
        <div id="admin-menu-dropdown" className="admin-menu__dropdown" role="menu">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                type="button"
                key={item.view}
                role="menuitem"
                className={`admin-menu__item${activeView === item.view ? " admin-menu__item--active" : ""}`}
                onClick={() => {
                  onSelect(item.view);
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
              >
                <Icon size={15} strokeWidth={2.5} aria-hidden="true" />
                {t(item.labelKey)}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
